import { loadStripe } from '@stripe/stripe-js';
import { api } from '@/lib/api';

// Utiliza a mesma variável VITE_STRIPE_PUBLISHABLE_KEY usada em outras partes do app
// Se estiver undefined, logamos um aviso para facilitar a depuração.
const publishableKey: string | undefined = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

if (!publishableKey) {
  console.warn('[StripeService] ⚠️  VITE_STRIPE_PUBLISHABLE_KEY não foi definido no ambiente. Verifique o arquivo .env e o docker-compose.yml');
}

const mode = import.meta.env.MODE;
const isDevelopment = mode === 'development';
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
const requiresHttps = !isDevelopment && !isLocalhost;
const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : '';

export const stripePromise = (() => {
  if (requiresHttps && currentProtocol && currentProtocol !== 'https:') {
    const httpsUrl = `https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
    console.error('[StripeService] Ambiente não seguro detectado, redirecionando para HTTPS');
    window.location.replace(httpsUrl);
    return Promise.resolve(null);
  }
  if (!publishableKey) {
    return Promise.resolve(null);
  }
  return loadStripe(publishableKey);
})();

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

// Stripe interfaces
interface StripeError {
  message: string;
  type?: string;
}

interface StripePaymentMethod {
  id: string;
  type: string;
}

interface StripePaymentIntent {
  id: string;
  status: string;
  amount: number;
}

interface StripeConfirmPaymentParams {
  elements: unknown;
  clientSecret: string;
  confirmParams: {
    return_url: string;
  };
  redirect: string;
}

interface StripeConfirmPaymentResult {
  error?: StripeError;
  paymentIntent?: StripePaymentIntent;
}

interface StripeCreatePaymentMethodParams {
  type: string;
  card: unknown;
}

interface StripeCreatePaymentMethodResult {
  error?: StripeError;
  paymentMethod?: StripePaymentMethod;
}

export interface CreatePaymentIntentResponse {
  success: boolean;
  client_secret: string;
  payment_intent_id: string;
  pagamento_id: string;
}

export interface PaymentIntentDetails {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: Date;
  metadata: Record<string, string>;
}

export class StripeService {
  // Obter instância do Stripe
  static async getStripe() {
    return await stripePromise;
  }

  // Criar Payment Intent para compra de créditos
  static async criarPaymentIntent(
    valor: number,
    userId: string,
    metadata?: Record<string, string>
  ): Promise<CreatePaymentIntentResponse> {
    try {
      const response = await api.post('/api/stripe/create-payment-intent', {
        valor,
        user_id: userId,
        currency: 'brl',
        metadata
      });

      return response.data;
    } catch (error: unknown) {
      console.error('Erro ao criar payment intent:', error);
      const message = error instanceof Error && 'response' in error && (error as ApiError).response?.data?.message || 'Erro ao criar intenção de pagamento';
      throw new Error(message);
    }
  }

  // Confirmar pagamento
  static async confirmarPagamento(clientSecret: string, paymentMethod: unknown) {
    try {
      
      
      const stripe = await this.getStripe();
      if (!stripe) {
        throw new Error('Stripe não foi carregado corretamente');
      }

      const result = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          payment_method: paymentMethod,
          return_url: `${window.location.origin}/client/creditos`
        }
      });

      
      return result;
    } catch (error: unknown) {
      console.error('Erro ao confirmar pagamento:', error);
      throw error;
    }
  }

  // Processar pagamento com Elements
  static async processarPagamentoComElements(
    stripe: unknown, 
    elements: unknown, 
    clientSecret: string
  ) {
    try {
      
      
      // PASSO 1: Submeter elements (obrigatório pela nova API Stripe)
      console.log('Submetendo elements...');
      const { error: submitError } = await (elements as { submit: () => Promise<{ error?: { message: string } }> }).submit();
      
      if (submitError) {
        console.error('Erro ao submeter elements:', submitError);
        throw new Error(submitError.message);
      }

      console.log('Elements submetidos com sucesso');
      
      // PASSO 2: Confirmar pagamento
      console.log('Confirmando pagamento...');
      const { error, paymentIntent } = await (stripe as { confirmPayment: (params: StripeConfirmPaymentParams) => Promise<StripeConfirmPaymentResult> }).confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/client/creditos?payment=success`
        },
        redirect: 'if_required'
      });

      if (error) {
        console.error('Erro ao confirmar pagamento:', error);
        throw new Error(error.message);
      }

      console.log('Pagamento confirmado com sucesso:', paymentIntent);
      return { paymentIntent, error: null };
    } catch (error: unknown) {
      console.error('Erro geral no processamento:', error);
      return { paymentIntent: null, error };
    }
  }

  // Buscar detalhes do Payment Intent
  static async buscarPaymentIntent(paymentIntentId: string): Promise<PaymentIntentDetails> {
    try {
      
      
      const response = await api.get(`/api/stripe/payment-intent/${paymentIntentId}`);
      
      
      return response.data.payment_intent;
    } catch (error: unknown) {
      console.error('Erro ao buscar payment intent:', error);
      const message = error instanceof Error && 'response' in error && (error as ApiError).response?.data?.message || 'Erro ao buscar detalhes do pagamento';
      throw new Error(message);
    }
  }

  // Processar reembolso (admin apenas)
  static async processarReembolso(
    paymentIntentId: string, 
    valor?: number, 
    motivo?: string
  ) {
    try {
      
      
      const response = await api.post('/api/stripe/refund', {
        payment_intent_id: paymentIntentId,
        valor,
        motivo
      });

      
      return response.data;
    } catch (error: unknown) {
      console.error('Erro ao processar reembolso:', error);
      const message = error instanceof Error && 'response' in error && (error as ApiError).response?.data?.message || 'Erro ao processar reembolso';
      throw new Error(message);
    }
  }

  // Validar cartão (para validação prévia)
  static async validarCartao(stripe: unknown, cardElement: unknown) {
    try {
      console.log('Validando cartão...');
      
      const { error, paymentMethod } = await (stripe as { createPaymentMethod: (params: StripeCreatePaymentMethodParams) => Promise<StripeCreatePaymentMethodResult> }).createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        console.error('Erro na validação do cartão:', error);
        return { valid: false, error: error.message };
      }

      console.log('Cartão validado com sucesso');
      return { valid: true, paymentMethod };
    } catch (error: unknown) {
      console.error('Erro geral na validação:', error);
      return { valid: false, error: error instanceof Error ? error.message : 'Erro desconhecido' };
    }
  }

  // Formatar valor para exibição
  static formatarValor(valor: number, moeda: string = 'BRL'): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: moeda
    }).format(valor);
  }

  // Obter status traduzido
  static obterStatusTraduzido(status: string): string {
    const statusMap: { [key: string]: string } = {
      'requires_payment_method': 'Aguardando método de pagamento',
      'requires_confirmation': 'Requer confirmação',
      'requires_action': 'Requer ação',
      'processing': 'Processando',
      'requires_capture': 'Requer captura',
      'canceled': 'Cancelado',
      'succeeded': 'Bem-sucedido'
    };

    return statusMap[status] || status;
  }
} 
